import { environment } from '../environments/environment';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  inject,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideHttpClient } from "@angular/common/http";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { provideApollo } from "apollo-angular";
import { ApolloLink, HttpLink, InMemoryCache, Observable } from "@apollo/client/core";
import { print } from "graphql";
import { onError } from "@apollo/client/link/error";
import { NgApexchartsModule } from "ng-apexcharts";
import { PublicLayoutComponent } from "./layouts/public-layout/public-layout.component";
import { LeafletModule } from "@bluehalo/ngx-leaflet";
import { CarouselModule } from "ngx-owl-carousel-o";
import { PublicHeader } from "./features/public/public-header/public-header";
import { PublicFooter } from "./features/public/public-footer/public-footer";
import { Gallery } from "./features/public/gallery/gallery";
import { Artform } from "./features/public/artform/artform";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { LoginModule } from "./dashboard/auth/login/login.module";
import { MatDialogModule } from '@angular/material/dialog';
import { AuthService } from "./core/services/auth.service";
import { refreshAccessToken, terminalRefreshFailure } from "./core/services/token-refresh";
import { SecurityUtils } from "./utils/security.utils";
import { LaunchModalComponent } from "./shared/components/launch-modal/launch-modal.component";
import { LoginNotificationModalComponent } from "./shared/components/login-notification-modal/login-notification-modal.component";
import { FormsModule } from '@angular/forms';
import { ChatbotComponent } from './shared/chatbot/chatbot.component';

@NgModule({
  declarations: [AppComponent, PublicLayoutComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgApexchartsModule,
    BrowserAnimationsModule,
    LeafletModule,
    CarouselModule,
    MatIconModule,
    LoginModule,
    PublicHeader,
    PublicFooter,
    Gallery,
    Artform,
    MatDialogModule,
    LaunchModalComponent,
    LoginNotificationModalComponent,
    FormsModule,
    ChatbotComponent,
  ],
  providers: [
    provideHttpClient(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideApollo(() => {
      const authService = inject(AuthService);

      const httpLink = new HttpLink({
        uri: `${environment.apiUrl}/graphql`,
        credentials: 'include',
      });

      // ── 1. Auth Link (Removed): Tokens are now handled automatically by HttpOnly Cookies ──

      // ── 2. Error Link: refresh on 401/UNAUTHENTICATED, retry once ────────────
      //
      // Tracks in-flight refresh so parallel failing requests queue up instead
      // of each triggering their own refresh call.
      let isRefreshing = false;
      const pendingQueue: (() => void)[] = [];

      const flushQueue = () => { pendingQueue.forEach(cb => cb()); pendingQueue.length = 0; };

      const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        // Never intercept login, logout or refresh mutations — they handle their own unauth errors
        const bypassOps = ['Login', 'EmailLogin', 'MobileLogin', 'RefreshToken', 'Logout'];
        if (bypassOps.includes(operation.operationName ?? '')) return;

        const isUnauth =
          graphQLErrors?.some((e: any) =>
            e.extensions?.code === 'UNAUTHENTICATED' ||
            e.message?.toLowerCase().includes('unauthorized') ||
            e.message?.toLowerCase().includes('no token') ||
            e.message?.toLowerCase().includes('invalid token'),
          ) ||
          (networkError as any)?.status === 401 ||
          (networkError as any)?.statusCode === 401;

        if (!isUnauth) return;

        // If the user isn't logged in to begin with, don't try to refresh or force a logout redirect.
        // Just let the 401 error pass through to the component.
        if (!authService.isLoggedIn()) {
          return;
        }

        // Return a new Observable that resolves after refresh completes
        return new Observable((observer: any) => {
          if (isRefreshing) {
            // Another request is already refreshing — queue this one
            pendingQueue.push(() => {
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            });
            return;
          }

          isRefreshing = true;

          refreshAccessToken(environment.apiUrl, (user) => {
            if (user.userId) authService.storeSession(user);
          })
            .then(outcome => {
              isRefreshing = false;
              const user = outcome.user;
              if (user?.userId) {
                flushQueue();

                // Retry the current operation (cookies will be attached automatically)
                forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                });
              } else if (terminalRefreshFailure(outcome.errorMessage)) {
                pendingQueue.length = 0;
                authService.logout();
                observer.error(networkError || graphQLErrors || new Error('Unauthorized'));
              } else {
                pendingQueue.length = 0;
                observer.error(networkError || graphQLErrors || new Error('Unauthorized'));
              }
            })
            .catch(() => {
              isRefreshing = false;
              pendingQueue.length = 0;
              observer.error(networkError || graphQLErrors || new Error('Unauthorized'));
            });
        });
      });

      const hmacLink = new ApolloLink((operation, forward) => {
        const requireHmacOps = ['CreateTroupe', 'CreateArtist', 'CreateAgency', 'AddTroupeMembersPublic'];
        if (requireHmacOps.includes(operation.operationName ?? '')) {
          // Reconstruct the exact body Apollo HttpLink sends
          const body = {
            operationName: operation.operationName,
            variables: operation.variables,
            query: print(operation.query),
          };
          const { signature, timestamp } = SecurityUtils.generateRequestSignature(body);
          
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              'x-request-signature': signature,
              'x-request-timestamp': timestamp,
            }
          }));
        }
        return forward(operation);
      });

      return {
        // Chain: hmac -> error → http
        link: ApolloLink.from([hmacLink, errorLink, httpLink]),
        cache: new InMemoryCache(),
      };
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule { }
